import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Users, Mail, BookOpen, MessagesSquare, MapPin, Megaphone, Send, Plus, Trash2, Zap } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

export default function AdminPage() {
  const { user, loading } = useAuth();
  const qc = useQueryClient();
  const [broadcast, setBroadcast] = useState({ subject: "", html: "" });
  const [ad, setAd] = useState({ title: "", image_url: "", link_url: "", placement: "sidebar" });

  const { data: stats } = useQuery({ queryKey: ["admin-stats"], queryFn: async () => (await api.get("/admin/stats")).data, enabled: !!user && user.role === "admin" });
  const { data: ads = [] } = useQuery({ queryKey: ["all-ads"], queryFn: async () => (await api.get("/ads")).data, enabled: !!user && user.role === "admin" });

  const sendBroadcast = useMutation({
    mutationFn: async () => (await api.post("/admin/broadcast", broadcast)).data,
    onSuccess: (d) => { toast.success(`Broadcast queued to ${d.recipients} recipients (${d.sent} sent)`); setBroadcast({ subject: "", html: "" }); },
    onError: (e) => toast.error(formatError(e.response?.data?.detail)),
  });

  const sendDaily = useMutation({
    mutationFn: async () => (await api.post("/admin/send-daily-code")).data,
    onSuccess: (d) => toast.success(d.status === "ok" ? `Daily code "${d.article}" sent to ${d.sent}` : d.status),
    onError: (e) => toast.error(formatError(e.response?.data?.detail)),
  });

  const createAd = useMutation({
    mutationFn: async () => (await api.post("/ads", ad)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["all-ads"] }); qc.invalidateQueries({ queryKey: ["ads"] }); toast.success("Ad created"); setAd({ title: "", image_url: "", link_url: "", placement: "sidebar" }); },
    onError: (e) => toast.error(formatError(e.response?.data?.detail)),
  });

  const delAd = useMutation({
    mutationFn: async (id) => api.delete(`/ads/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["all-ads"] }); qc.invalidateQueries({ queryKey: ["ads"] }); toast.success("Ad removed"); },
  });

  if (loading) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;

  const statCards = [
    { icon: Users, label: "Members", val: stats?.members },
    { icon: Mail, label: "Subscribers", val: stats?.subscribers },
    { icon: BookOpen, label: "Articles", val: stats?.articles },
    { icon: MessagesSquare, label: "Threads", val: stats?.threads },
    { icon: MapPin, label: "Listings", val: stats?.listings },
    { icon: Megaphone, label: "Ads", val: stats?.ads },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <span className="label-tech text-primary">Control Room</span>
      <h1 className="font-display text-4xl lg:text-5xl tracking-tighter font-black mt-2 mb-8">Admin Dashboard</h1>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-0 border border-border mb-12">
        {statCards.map((s) => (
          <div key={s.label} className="p-6 border-border border-r border-b md:border-b-0 last:border-r-0" data-testid={`stat-${s.label.toLowerCase()}`}>
            <s.icon className="w-5 h-5 text-primary mb-3" />
            <p className="font-display text-3xl font-black">{s.val ?? "—"}</p>
            <p className="label-tech text-muted-foreground mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Email tools */}
        <div className="space-y-8">
          <section className="border border-border bg-white p-6">
            <h2 className="font-display text-xl font-bold flex items-center gap-2 mb-4"><Send className="w-5 h-5 text-primary" /> Newsletter Broadcast</h2>
            <div className="space-y-3">
              <div>
                <Label className="label-tech">Subject</Label>
                <Input value={broadcast.subject} onChange={(e) => setBroadcast({ ...broadcast, subject: e.target.value })} className="rounded-none mt-1" data-testid="broadcast-subject" />
              </div>
              <div>
                <Label className="label-tech">Message (HTML allowed)</Label>
                <Textarea value={broadcast.html} onChange={(e) => setBroadcast({ ...broadcast, html: e.target.value })} rows={5} className="rounded-none mt-1" data-testid="broadcast-body" />
              </div>
              <Button onClick={() => sendBroadcast.mutate()} disabled={sendBroadcast.isPending || !broadcast.subject} className="rounded-none bg-primary hover:bg-[#D62828] font-bold w-full" data-testid="broadcast-send-btn">
                {sendBroadcast.isPending ? "Sending…" : "Send to All Members"}
              </Button>
            </div>
          </section>

          <section className="border border-border bg-white p-6">
            <h2 className="font-display text-xl font-bold flex items-center gap-2 mb-2"><Zap className="w-5 h-5 text-primary" /> Daily NFPA Code</h2>
            <p className="text-sm text-muted-foreground mb-4">Sends a random NFPA code snippet to all members now. (Also runs automatically each day.)</p>
            <Button onClick={() => sendDaily.mutate()} disabled={sendDaily.isPending} variant="outline" className="rounded-none border-foreground font-bold w-full" data-testid="send-daily-btn">
              {sendDaily.isPending ? "Sending…" : "Send Daily Code Now"}
            </Button>
          </section>
        </div>

        {/* Ads manager */}
        <section className="border border-border bg-white p-6">
          <h2 className="font-display text-xl font-bold flex items-center gap-2 mb-4"><Megaphone className="w-5 h-5 text-primary" /> Advertising Space</h2>
          <div className="space-y-3 mb-6">
            <div>
              <Label className="label-tech">Ad Title</Label>
              <Input value={ad.title} onChange={(e) => setAd({ ...ad, title: e.target.value })} className="rounded-none mt-1" data-testid="ad-title-input" />
            </div>
            <div>
              <Label className="label-tech">Image URL</Label>
              <Input value={ad.image_url} onChange={(e) => setAd({ ...ad, image_url: e.target.value })} className="rounded-none mt-1" data-testid="ad-image-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-tech">Link URL</Label>
                <Input value={ad.link_url} onChange={(e) => setAd({ ...ad, link_url: e.target.value })} className="rounded-none mt-1" data-testid="ad-link-input" />
              </div>
              <div>
                <Label className="label-tech">Placement</Label>
                <select value={ad.placement} onChange={(e) => setAd({ ...ad, placement: e.target.value })} className="w-full h-10 border border-input px-3 mt-1 rounded-none bg-white" data-testid="ad-placement-select">
                  <option value="sidebar">Sidebar</option>
                  <option value="banner">Banner</option>
                </select>
              </div>
            </div>
            <Button onClick={() => createAd.mutate()} disabled={createAd.isPending || !ad.title || !ad.image_url} className="rounded-none bg-primary hover:bg-[#D62828] font-bold w-full" data-testid="ad-create-btn">
              <Plus className="w-4 h-4 mr-1" /> Create Ad
            </Button>
          </div>
          <div className="space-y-2">
            {ads.map((a) => (
              <div key={a.id} className="flex items-center gap-3 border border-border p-2" data-testid={`admin-ad-${a.id}`}>
                <img src={a.image_url} alt="" className="w-12 h-12 object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{a.title}</p>
                  <p className="label-tech text-muted-foreground">{a.placement}</p>
                </div>
                <Button size="icon" variant="ghost" className="rounded-none text-primary" onClick={() => delAd.mutate(a.id)} data-testid={`del-ad-${a.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
